import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  dashApi, type DecisionRecord, type OutcomeComparison, type ScenarioQualityRow,
} from "@/lib/dashApi";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, StatusPill, Stat } from "@/components/dash/ui";

const CATEGORIES = ["finance", "sales", "renewals", "repairs", "support", "catalogue", "staffing",
  "customer", "data_quality", "reconciliation", "platform", "other"];
// Mirrors backend decisions.STATUS_TRANSITIONS (approve/review/cancel/supersede use dedicated buttons).
const NEXT: Record<string, string[]> = {
  draft: ["proposed"], proposed: ["draft"], approved: ["in_progress", "awaiting_review"],
  in_progress: ["awaiting_review"], awaiting_review: ["in_progress"], reviewed: ["awaiting_review"],
  superseded: [], cancelled: [],
};

export default function DecisionRecordsPage() {
  const [tab, setTab] = useState<"decisions" | "quality">("decisions");
  return (
    <div className="space-y-6">
      <PageHeader
        title="Decision Records & Outcome Learning"
        description="Record a management decision (a choice, not a source-system action), link it to scenarios and actions, and later compare the scenario expectation with a measured actual. Outcome comparison reports variance only — it never claims the decision caused the result. IRONDash-only; no source writes."
      />
      <div className="flex gap-2 text-sm">
        <button onClick={() => setTab("decisions")} className={`rounded px-3 py-1 ${tab === "decisions" ? "bg-sky-500/20 text-sky-200" : "bg-slate-800 text-slate-400"}`}>Decision Records</button>
        <button onClick={() => setTab("quality")} className={`rounded px-3 py-1 ${tab === "quality" ? "bg-sky-500/20 text-sky-200" : "bg-slate-800 text-slate-400"}`}>Scenario Quality</button>
      </div>
      {tab === "decisions" ? <DecisionsTab /> : <ScenarioQualityTab />}
    </div>
  );
}

function DecisionsTab() {
  const qc = useQueryClient();
  const summary = useQuery({ queryKey: ["dec-summary"], queryFn: dashApi.decisionsSummary });
  const [statusFilter, setStatusFilter] = useState("");
  const list = useQuery({ queryKey: ["decisions", statusFilter], queryFn: () => dashApi.decisionRecords({ status: statusFilter || undefined }) });
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("sales");
  const create = useMutation({
    mutationFn: () => dashApi.createDecision({ title, category }),
    onSuccess: () => { setTitle(""); qc.invalidateQueries({ queryKey: ["decisions"] }); qc.invalidateQueries({ queryKey: ["dec-summary"] }); },
  });
  const s = summary.data;
  return (
    <>
      {s && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Stat label="Active decisions" value={s.active_count} />
          <Stat label="Awaiting review" value={s.awaiting_review} />
          <Stat label="Review overdue" value={s.review_overdue} sub={s.review_overdue ? "needs attention" : undefined} />
          <Stat label="Proposed" value={s.proposed} />
          <Stat label="Approved, no owner" value={s.approved_unowned} />
        </div>
      )}
      <Panel title="Create a decision" subtitle="Records a management choice — never changes a source system or the linked scenario/action.">
        <div className="flex flex-wrap items-end gap-2">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Decision title" className="flex-1 rounded border border-slate-700 bg-slate-800 px-2 py-1.5 text-sm text-slate-200" />
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="rounded border border-slate-700 bg-slate-800 px-2 py-1.5 text-sm text-slate-200">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={() => create.mutate()} disabled={create.isPending || !title.trim()} className="rounded bg-sky-500/20 px-3 py-1.5 text-sm text-sky-200 disabled:opacity-40">Create draft</button>
        </div>
      </Panel>
      <Panel title="Decisions" subtitle="Open a decision to manage its lifecycle, links, outcomes and review.">
        <div className="mb-3 flex flex-wrap gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200">
            <option value="">Any status</option>
            {["draft", "proposed", "approved", "in_progress", "awaiting_review", "reviewed", "superseded", "cancelled"].map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
        {list.isLoading ? <LoadingState /> : list.isError ? <ErrorState error={list.error} /> : (
          (list.data?.decisions.length ?? 0) === 0 ? <EmptyState>No decisions yet.</EmptyState>
            : <div className="space-y-2">{list.data!.decisions.map((d) => <DecisionRow key={d.id} d={d} />)}</div>
        )}
      </Panel>
    </>
  );
}

function DecisionRow({ d }: { d: DecisionRecord }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/30">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full flex-wrap items-center gap-2 px-3 py-2 text-left">
        <StatusPill status={d.status} />
        <span className="flex-1 truncate text-sm text-slate-200">{d.title}</span>
        <span className="text-[11px] text-slate-500">{d.category}</span>
        {d.owner ? <span className="text-[11px] text-slate-400">👤 {d.owner}</span> : <span className="text-[11px] text-amber-400">unowned</span>}
        {d.review_overdue && <span className="text-[11px] text-rose-400">review overdue</span>}
        {d.target_review_date && <span className="text-[11px] text-slate-500">review {d.target_review_date}</span>}
      </button>
      {open && <DecisionDetail d={d} />}
    </div>
  );
}

function DecisionDetail({ d }: { d: DecisionRecord }) {
  const qc = useQueryClient();
  const full = useQuery({ queryKey: ["decision", d.id], queryFn: () => dashApi.decisionRecord(d.id) });
  const outcomes = useQuery({ queryKey: ["decision-outcomes", d.id], queryFn: () => dashApi.decisionOutcomes(d.id) });
  const [err, setErr] = useState<string | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const inv = () => { ["decision", "decision-outcomes"].forEach((k) => qc.invalidateQueries({ queryKey: [k, d.id] })); qc.invalidateQueries({ queryKey: ["decisions"] }); qc.invalidateQueries({ queryKey: ["dec-summary"] }); };
  const run = (p: Promise<unknown>) => p.then(() => { setErr(null); inv(); }).catch((e) => setErr(e instanceof Error ? e.message : "failed"));

  return (
    <div className="space-y-3 border-t border-slate-800 px-3 py-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        {(NEXT[d.status] ?? []).map((st) => (
          <button key={st} onClick={() => run(dashApi.decisionTransition(d.id, st))} className="rounded bg-slate-700 px-2 py-0.5 text-slate-300">→ {st}</button>
        ))}
        {["proposed", "in_progress", "awaiting_review"].includes(d.status) && (
          <button onClick={() => run(dashApi.decisionApprove(d.id))} className="rounded bg-emerald-500/15 px-2 py-0.5 text-emerald-300">approve</button>
        )}
        {!["cancelled", "superseded"].includes(d.status) && (
          <button onClick={() => run(dashApi.decisionSupersede(d.id))} className="rounded bg-slate-700 px-2 py-0.5 text-slate-400">supersede</button>
        )}
      </div>
      {err && <p className="text-rose-300">{err}</p>}

      {full.data && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
          <F k="Rationale" v={full.data.decision.rationale} />
          <F k="Expected outcome" v={full.data.decision.expected_outcome} />
          <F k="Key assumptions" v={full.data.decision.key_assumptions} />
          <F k="Risks accepted" v={full.data.decision.risks_accepted} />
          <F k="Review note" v={full.data.decision.review_note} />
          <F k="Cancel reason" v={full.data.decision.cancel_reason} />
          <F k="Approved at" v={full.data.decision.approved_at} />
          <F k="Superseded by" v={full.data.decision.superseded_by ? `#${full.data.decision.superseded_by}` : null} />
        </dl>
      )}

      {/* Review + cancel (both require text) */}
      {["approved", "in_progress", "awaiting_review"].includes(d.status) && (
        <div className="flex flex-wrap items-end gap-2">
          <input value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} placeholder="Review note (required to mark reviewed)" className="flex-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-slate-200" />
          <button onClick={() => reviewNote.trim() && run(dashApi.decisionReview(d.id, reviewNote))} className="rounded bg-sky-500/20 px-2 py-1 text-sky-200">mark reviewed</button>
        </div>
      )}
      {!["cancelled", "superseded", "reviewed"].includes(d.status) && (
        <div className="flex flex-wrap items-end gap-2">
          <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Cancel reason (required)" className="flex-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-slate-200" />
          <button onClick={() => cancelReason.trim() && run(dashApi.decisionCancel(d.id, cancelReason))} className="rounded bg-rose-500/15 px-2 py-1 text-rose-300">cancel</button>
        </div>
      )}

      {/* Linked scenario outcomes */}
      <div>
        <p className="mb-1 text-slate-500">Linked scenario outcomes</p>
        {(outcomes.data?.outcomes.length ?? 0) === 0 ? <p className="text-slate-600">No linked scenarios (link a scenario to compare outcomes).</p>
          : <div className="space-y-2">{outcomes.data!.outcomes.map((o) => <OutcomeBlock key={o.scenario_id} o={o} />)}</div>}
      </div>

      {full.data && (
        <div><p className="mb-1 text-slate-500">Links</p>
          {full.data.links.length === 0 ? <p className="text-slate-600">No links.</p>
            : <ul className="space-y-0.5">{full.data.links.map((l) => <li key={l.id} className="text-slate-400"><span className="text-slate-300">{l.link_type}</span>: {l.label || l.link_ref}</li>)}</ul>}
        </div>
      )}
    </div>
  );
}

function OutcomeBlock({ o }: { o: OutcomeComparison }) {
  return (
    <div className="rounded border border-slate-800 bg-slate-900/40 p-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-slate-300">Scenario #{o.scenario_id} ({o.scenario_type})</span>
        <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[11px] text-slate-300">maturity: {o.maturity}</span>
        {o.accuracy_label && <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[11px] text-sky-200">{o.accuracy_label}</span>}
        {o.scope_status && <span className="text-[11px] text-slate-500">scope: {o.scope_status}</span>}
      </div>
      <div className="mt-1 grid grid-cols-2 gap-2 text-[11px]">
        <div><span className="text-slate-500">expected:</span> {JSON.stringify(o.expected)}</div>
        <div><span className="text-slate-500">actual:</span> {JSON.stringify(o.actual)}</div>
      </div>
      {o.variance != null && <p className="mt-1 text-[11px] text-slate-400">variance {o.variance}{o.variance_pct != null ? ` (${o.variance_pct}%)` : ""}</p>}
      <ul className="mt-1 space-y-0.5">{o.caveats.map((c, i) => <li key={i} className="text-[10px] text-slate-500">• {c}</li>)}</ul>
    </div>
  );
}

function F({ k, v }: { k: string; v: string | null }) {
  if (!v) return null;
  return <div><dt className="text-slate-500">{k}</dt><dd className="text-slate-300">{v}</dd></div>;
}

function ScenarioQualityTab() {
  const [scope, setScope] = useState("");
  const q = useQuery({ queryKey: ["scenario-quality", scope], queryFn: () => dashApi.scenarioQuality({ scope_status: scope || undefined }) });
  return (
    <Panel title="Scenario Quality" subtitle="Scope status, live trust, assumptions and outcome maturity per scenario.">
      <div className="mb-3 flex flex-wrap gap-2">
        <select value={scope} onChange={(e) => setScope(e.target.value)} className="rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200">
          <option value="">Any scope</option>
          {["fully_scoped", "partially_scoped", "all_entity", "unsupported_scope", "insufficient_data"].map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : (
        (q.data?.scenarios.length ?? 0) === 0 ? <EmptyState>No scenarios.</EmptyState> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                <th className="pb-2 pr-3">Scenario</th><th className="pb-2 pr-3">Type</th><th className="pb-2 pr-3">Scope</th>
                <th className="pb-2 pr-3">Trust</th><th className="pb-2 pr-3">Manual/Total</th><th className="pb-2 pr-3">Maturity</th><th className="pb-2">Accuracy</th>
              </tr></thead>
              <tbody>
                {q.data!.scenarios.map((r: ScenarioQualityRow) => (
                  <tr key={r.id} className="border-t border-slate-800 align-top">
                    <td className="py-1.5 pr-3 text-slate-200">{r.title}{r.scope_note && <div className="text-[10px] text-slate-500">{r.scope_note}</div>}</td>
                    <td className="py-1.5 pr-3 text-slate-500">{r.scenario_type}</td>
                    <td className="py-1.5 pr-3"><ScopeBadge s={r.scope_status} /></td>
                    <td className="py-1.5 pr-3 text-slate-400">{r.trust}</td>
                    <td className="py-1.5 pr-3 text-slate-400">{r.manual_assumption_count}/{r.assumption_count}</td>
                    <td className="py-1.5 pr-3 text-slate-400">{r.maturity ?? "—"}</td>
                    <td className="py-1.5 text-slate-400">{r.accuracy_label ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </Panel>
  );
}

function ScopeBadge({ s }: { s: string | null }) {
  const tone: Record<string, string> = {
    fully_scoped: "text-emerald-300", partially_scoped: "text-amber-300", all_entity: "text-slate-400",
    unsupported_scope: "text-rose-300", insufficient_data: "text-rose-300",
  };
  return <span className={`text-[11px] ${tone[s ?? "all_entity"] ?? "text-slate-400"}`}>{s ?? "all_entity"}</span>;
}
